import Config from '@config';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  DebtDynamics,
  GetPersonalAccountPaymentsArgs,
  Ledger,
  PersonalAccount,
  PersonalAccountDetails,
  PersonalAccountPayment,
  PersonalAccountSlim,
} from './types';

export const personalAccountsApi = createApi({
  reducerPath: 'personalAccountsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/accounts',
  }),
  tagTypes: ['PersonalAccounts', 'Charges', 'DebtDynamics'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getPersonalAccounts: builder.query<PersonalAccount[], void>({
      query: () => ({ url: '' }),
      providesTags: ['PersonalAccounts'],
    }),
    getPersonalAccountsI: builder.infiniteQuery<
      PersonalAccountSlim[],
      FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: ``,
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PersonalAccounts'],
    }),
    getPersonalAccountById: builder.query<PersonalAccountDetails, string>({
      query: id => ({ url: `/${id}` }),
    }),
    getLedger: builder.query<Ledger[], { accountId: string }>({
      query: ({ accountId }) => ({ url: `/${accountId}/ledger` }),
      providesTags: ['Charges'],
    }),
    getDebtDynamics: builder.query<DebtDynamics[], { accountId: string }>({
      query: ({ accountId, ...params }) => ({
        url: `/${accountId}/debt-dynamics`,
        params,
      }),
      providesTags: ['DebtDynamics'],
    }),
    getPersonalAccountPayments: builder.query<
      PersonalAccountPayment[],
      GetPersonalAccountPaymentsArgs
    >({
      query: ({ accountId, ...params }) => ({
        url: `/${accountId}/payment-history`,
        params,
      }),
    }),
  }),
});

export const {
  useGetPersonalAccountsQuery,
  useGetPersonalAccountByIdQuery,
  useGetLedgerQuery,
  useGetDebtDynamicsQuery,
  useGetPersonalAccountPaymentsQuery,
} = personalAccountsApi;

export const useGetPersonalAccountsIInfiniteQuery = normalizeInfiniteQueryData(
  personalAccountsApi.useGetPersonalAccountsIInfiniteQuery,
);
